import { useContext, useState, useEffect } from 'react';
import { GlobalContext } from '../context/GlobalContext';
import { TabBar } from '../components/TabBar';
import { EmptyState } from '../components/EmptyState';
import {
	Download,
	ExternalLink,
	FileImage,
	FileText,
	Image as ImageIcon,
	Megaphone,
	Palette,
	Sparkles,
	Clock
} from 'lucide-react';

const ALL_TABS = [
	{ id: 'active', label: 'Activas', icon: Sparkles },
	{ id: 'upcoming', label: 'Próximas', icon: Clock },
	{ id: 'materials', label: 'Material', icon: Palette }
];

interface Entry {
	title?: string;
	subtitle?: string;
	description?: string;
	thumbnail?: string;
	download_url?: string;
}

const ICON_STYLES: Record<string, { bg: string; text: string }> = {
	active: { bg: 'bg-emerald-500/10', text: 'text-emerald-400' },
	upcoming: { bg: 'bg-amber-500/10', text: 'text-amber-400' },
	materials: { bg: 'bg-violet-500/10', text: 'text-violet-400' }
};

function isImageUrl(url?: string): boolean {
	if (!url) return false;
	return /\.(png|jpe?g|gif|webp|svg|bmp)(\?|$)/i.test(url);
}

function DocIcon({ url }: { url?: string }) {
	if (url && isImageUrl(url)) return <FileImage className="size-6" />;
	return <FileText className="size-6" />;
}

function ListRow({ entry, imageId, isLoaded, onLoad, section }: {
	entry: Entry;
	imageId: string;
	isLoaded: boolean;
	onLoad: (id: string) => void;
	section: string;
}) {
	const hasLink = !!(entry.download_url || entry.thumbnail);
	const style = ICON_STYLES[section] ?? ICON_STYLES.materials;
	const showAsImage = isImageUrl(entry.thumbnail);

	return (
		<div
			className={`group/row flex items-center gap-3 rounded-xl px-3 py-2.5 ring-1 ring-inset ring-white/[0.04] transition-all duration-150 hover:bg-white/[0.03] hover:ring-white/[0.08] ${hasLink ? 'cursor-pointer' : ''}`}
			onClick={() => {
				if (entry.download_url) window.open(entry.download_url, '_blank');
				else if (entry.thumbnail) window.open(entry.thumbnail, '_blank');
			}}
		>
			{entry.thumbnail && showAsImage ? (
				<div className={`flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-lg ${!isLoaded ? 'animate-pulse bg-neutral-800/40' : ''}`}>
					<img
						src={entry.thumbnail}
						alt={entry.title || 'Material'}
						className="size-12 rounded-lg object-cover"
						loading="lazy"
						onLoad={() => onLoad(imageId)}
					/>
				</div>
			) : (
				<div className={`flex size-12 shrink-0 items-center justify-center rounded-lg ${style.bg} ${style.text}`}>
					<DocIcon url={entry.download_url || entry.thumbnail} />
				</div>
			)}

			<div className="min-w-0 flex-1">
				<p className="truncate text-sm font-medium text-neutral-100">
					{entry.title?.trim() || 'Sin título'}
				</p>
				{(entry.subtitle || entry.description) && (
					<p className="mt-0.5 truncate text-xs text-neutral-500">
						{entry.subtitle?.trim() || entry.description?.trim()}
					</p>
				)}
			</div>

			{hasLink && (
				<div className="flex shrink-0 items-center gap-1 text-neutral-500 opacity-0 transition-opacity group-hover/row:opacity-100">
					{entry.download_url
						? <Download className="size-4" />
						: <ExternalLink className="size-4" />
					}
				</div>
			)}
		</div>
	);
}

export default function Promociones() {
	const { CountrySettings } = useContext(GlobalContext);
	const [loadedImages, setLoadedImages] = useState<Record<string, boolean>>({});
	const [activeTab, setActiveTab] = useState('active');

	const handleImageLoad = (id: string) => {
		setLoadedImages((prev) => ({ ...prev, [id]: true }));
	};

	const activePromos: Entry[] = CountrySettings?.announcements?.active_promos ?? [];
	const upcomingPromos: Entry[] = CountrySettings?.announcements?.upcoming_promos ?? [];
	const marketingMaterials: Entry[] = CountrySettings?.announcements?.marketing_material ?? [];

	const tabCounts: Record<string, number> = {
		active: activePromos.length,
		upcoming: upcomingPromos.length,
		materials: marketingMaterials.length
	};

	const visibleTabs = ALL_TABS
		.filter((t) => tabCounts[t.id] > 0)
		.map((t) => ({ ...t, badge: tabCounts[t.id] }));

	const needsTabCorrection = visibleTabs.length > 0 && !visibleTabs.find((t) => t.id === activeTab);
	const effectiveTab = needsTabCorrection ? visibleTabs[0]?.id ?? activeTab : activeTab;

	useEffect(() => {
		if (needsTabCorrection && visibleTabs[0]) setActiveTab(visibleTabs[0].id);
	}, [needsTabCorrection]);

	if (!CountrySettings) {
		return (
			<div className="mx-auto w-full max-w-7xl p-4 md:p-6">
				<div className="mb-6">
					<div className="h-8 w-64 animate-pulse rounded-lg bg-neutral-800/60" />
					<div className="mt-2 h-4 w-96 animate-pulse rounded-md bg-neutral-800/40" />
				</div>
				<div className="mb-6 flex gap-2">
					{[1, 2, 3].map((i) => (
						<div key={i} className="h-9 w-24 animate-pulse rounded-lg bg-neutral-800/40" />
					))}
				</div>
				<div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
					{[1, 2, 3, 4, 5, 6].map((i) => (
						<div
							key={i}
							className="flex items-center gap-3 rounded-xl px-3 py-2.5 ring-1 ring-inset ring-white/[0.04]"
						>
							<div className="size-12 shrink-0 animate-pulse rounded-lg bg-neutral-800/50" />
							<div className="min-w-0 flex-1">
								<div className="h-4 w-3/4 animate-pulse rounded bg-neutral-800/50" />
								<div className="mt-1.5 h-3 w-1/2 animate-pulse rounded bg-neutral-800/30" />
							</div>
						</div>
					))}
				</div>
			</div>
		);
	}

	const allEmpty = activePromos.length === 0 && upcomingPromos.length === 0 && marketingMaterials.length === 0;

	if (allEmpty) {
		return (
			<div className="mx-auto w-full max-w-7xl p-4 md:p-6">
				<div className="mb-6">
					<h1 className="text-2xl font-semibold text-neutral-100">Material Publicitario</h1>
					<p className="mt-1 text-sm text-neutral-400">
						Promociones, anuncios y material de mercadeo para tu tienda.
					</p>
				</div>
				<EmptyState
					icon={Megaphone}
					title="Sin material disponible"
					subtitle="No hay anuncios ni material publicitario por el momento."
				/>
			</div>
		);
	}

	if (visibleTabs.length === 0) return null;

	const currentEntries = effectiveTab === 'active'
		? activePromos
		: effectiveTab === 'upcoming'
			? upcomingPromos
			: marketingMaterials;

	const entryPrefix = effectiveTab === 'active' ? 'active' : effectiveTab === 'upcoming' ? 'upcoming' : 'marketing';

	return (
		<div className="mx-auto w-full max-w-7xl p-4 md:p-6">
			<div className="mb-6">
				<h1 className="text-2xl font-semibold text-neutral-100">Material Publicitario</h1>
				<p className="mt-1 text-sm text-neutral-400">
					Promociones, anuncios y material de mercadeo para tu tienda.
				</p>
			</div>

			{visibleTabs.length > 1 && (
				<div className="mb-6">
					<TabBar
						tabs={visibleTabs}
						activeTab={effectiveTab}
						onTabChange={setActiveTab}
					/>
				</div>
			)}

			{currentEntries.length > 0 ? (
				<div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
					{currentEntries.map((entry, index) => {
						const imageId = `${entryPrefix}-${index}-${entry.title}`;
						return (
							<ListRow
								key={imageId}
								entry={entry}
								imageId={imageId}
								isLoaded={!!loadedImages[imageId]}
								onLoad={handleImageLoad}
								section={effectiveTab}
							/>
						);
					})}
				</div>
			) : (
				<EmptyState
					icon={ImageIcon}
					title="Sin contenido"
					subtitle="No hay contenido disponible en esta sección."
				/>
			)}
		</div>
	);
}
